import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../api/admin.api';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import ImageZoomModal from '../../components/common/ImageZoomModal';
import ProductThumbnail from '../../components/product/ProductThumbnail';
import { formatPrice } from '../../utils/formatPrice';
import { getProductImage } from '../../utils/productImage';
import './Products.css';

const getProductName = (product) => product.title || product.name || 'Product';
const getProductStock = (product) => {
  const stock = Number(product.stock ?? 0);
  return Number.isFinite(stock) ? stock : 0;
};
const isOutOfStockProduct = (product) => getProductStock(product) <= 0;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOutOfStockOnly, setShowOutOfStockOnly] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await adminAPI.getProducts();
      const productsList = data?.items || data?.products || data?.data || data || [];
      setProducts(Array.isArray(productsList) ? productsList : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      console.error('Error details:', error.response?.data);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await adminAPI.deleteProduct(id);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };

  const handleOpenImage = (product) => {
    const image = getProductImage(product);

    if (!image) {
      return;
    }

    const title = getProductName(product);
    setZoomImage({ src: image, alt: title, title });
  };

  const sortedProducts = useMemo(() => products
    .map((product, index) => ({ product, index }))
    .sort((first, second) => {
      const firstCreated = Date.parse(first.product.createdAt || '') || 0;
      const secondCreated = Date.parse(second.product.createdAt || '') || 0;

      if (firstCreated !== secondCreated) {
        return secondCreated - firstCreated;
      }

      return first.index - second.index;
    })
    .map((item) => item.product), [products]);

  const outOfStockCount = useMemo(
    () => sortedProducts.filter(isOutOfStockProduct).length,
    [sortedProducts]
  );

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    let nextProducts = showOutOfStockOnly
      ? sortedProducts.filter(isOutOfStockProduct)
      : sortedProducts;

    if (!query) {
      return nextProducts;
    }

    const terms = query.split(/\s+/).filter(Boolean);
    return nextProducts.filter((product) => {
      const searchableText = [
        product.title,
        product.name,
        product.specialCode,
        product.productCode,
        product.code,
        product.brand,
        product.category,
      ].map((value) => String(value || '').toLowerCase()).join(' ');

      return terms.every((term) => searchableText.includes(term));
    });
  }, [sortedProducts, searchQuery, showOutOfStockOnly]);

  if (loading) return <Loader />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Products</h1>
        <Link to="/admin/products/create">
          <Button>Create New Product</Button>
        </Link>
      </div>

      <div className="admin-products__filters">
        <label style={{ display: 'grid', gap: '6px', fontWeight: 700 }}>
          <span>Search products</span>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search by name, code, brand, or category"
            style={{
              width: '100%',
              height: '46px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '0 14px',
              font: 'inherit',
            }}
          />
        </label>
        <div className="admin-products__filter-actions">
          <button
            type="button"
            className={`admin-products__stock-filter ${showOutOfStockOnly ? 'is-active' : ''}`}
            onClick={() => setShowOutOfStockOnly((current) => !current)}
            aria-pressed={showOutOfStockOnly}
          >
            {showOutOfStockOnly ? 'Show all products' : `Show Stock 0 (${outOfStockCount})`}
          </button>
          <span className="admin-products__result-count">
            {filteredProducts.length} shown / {products.length} total
          </span>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd' }}>
            <th style={{ padding: '10px', textAlign: 'left' }}>Photo</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Code</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Price</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Sale %</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Sale Price</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Category</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Stock</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <tr key={product.id || product._id} style={{ borderBottom: '1px solid #ddd' }}>
                <td className="admin-products__image-cell" style={{ padding: '10px' }}>
                  <button
                    type="button"
                    className="admin-products__image-button"
                    onClick={() => handleOpenImage(product)}
                    disabled={!getProductImage(product)}
                    aria-label={`Zoom ${getProductName(product)} photo`}
                  >
                    <ProductThumbnail product={product} size="admin" />
                  </button>
                </td>
                <td style={{ padding: '10px' }}>{getProductName(product)}</td>
                <td style={{ padding: '10px' }}>{product.specialCode || product.productCode || product.code || '-'}</td>
                <td style={{ padding: '10px' }}>{formatPrice(product.price)}</td>
                <td style={{ padding: '10px' }}>{product.salePercentage || 0}%</td>
                <td style={{ padding: '10px' }}>
                  {Number(product.salePrice || 0) > 0 ? formatPrice(product.salePrice) : '-'}
                </td>
                <td style={{ padding: '10px' }}>{product.category}</td>
                <td style={{ padding: '10px' }}>{product.stock ?? 0}</td>
                <td style={{ padding: '10px' }}>
                  <Link to={`/admin/products/${product.id || product._id}/edit`} style={{ marginRight: '8px' }}>
                    <Button variant="secondary">Edit</Button>
                  </Link>
                  <Button variant="danger" onClick={() => handleDelete(product.id || product._id)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" style={{ padding: '20px', textAlign: 'center' }}>
                {showOutOfStockOnly
                  ? 'No Stock 0 products match your filters'
                  : searchQuery ? 'No products match your search' : 'No products found'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <ImageZoomModal image={zoomImage} onClose={() => setZoomImage(null)} />
    </div>
  );
};

export default Products;
